import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Building2,
  Home,
  Paintbrush,
  HardHat,
  Zap,
  ClipboardList,
  Hammer,
  Wrench,
  CheckCircle,
  Phone,
  Calendar,
  Target,
  Shield,
  Award,
  Clock,
  Users,
  Truck,
  Ruler,
} from "lucide-react";
import { ServiceCard } from "../components/ServiceCard";
import { ProjectCard } from "../components/ProjectCard";
import { TestimonialCard } from "../components/TestimonialCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for dynamic services
const iconMap = {
  HardHat, Building2, Home, Paintbrush, Zap, ClipboardList, 
  Hammer, Wrench, Target, Shield, Award, Clock, Users, Truck, Ruler
};

// Default content fallbacks
const defaultContent = {
  hero: {
    subtitle: "Astute Builders LLC",
    title: "Building Excellence from Concept to Completion",
    highlight: "Concept to Completion",
    description: "Premium general contracting and construction services delivering precision, integrity, and unmatched quality across New Jersey.",
    image_url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920",
  },
  about_preview: {
    subtitle: "About Us",
    title: "Your Trusted Partner in Construction",
    description: "Astute Builders LLC is a full-service general contracting firm delivering high-quality residential and commercial construction solutions.",
    features: ["Licensed & Insured", "On-Time Delivery", "Transparent Pricing", "Quality Craftsmanship"],
    image_url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800",
    years_experience: "15+",
  },
  stats: [
    { number: "250+", label: "Projects Completed" },
    { number: "15+", label: "Years Experience" },
    { number: "100%", label: "Client Satisfaction" },
    { number: "50+", label: "Expert Team Members" },
  ],
  services: {
    subtitle: "What We Do",
    title: "Our Services",
    description: "Comprehensive construction services tailored to meet your residential and commercial needs.",
  },
  cta: {
    title: "Ready to Build Your Vision?",
    description: "Book a free 30-minute consultation to discuss your project requirements, timeline, and budget.",
  },
};

const defaultServices = [
  { title: "General Contracting", description: "Full-service general contracting for projects of all sizes.", icon: "HardHat" },
  { title: "Commercial Construction", description: "Expert commercial construction services for offices and retail.", icon: "Building2" },
  { title: "Residential Renovations", description: "Transform your home with comprehensive renovation services.", icon: "Home" },
  { title: "Interior Build-Outs", description: "Custom interior build-outs that maximize functionality.", icon: "Paintbrush" },
  { title: "Electrical Coordination", description: "Seamless electrical contracting coordination.", icon: "Zap" },
  { title: "Project Management", description: "End-to-end project management on time and within budget.", icon: "ClipboardList" },
];

export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState(defaultContent);
  const [services, setServices] = useState(defaultServices);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database first
        await axios.post(`${API}/seed`);
        await axios.post(`${API}/seed-content`);
        
        // Fetch all data in parallel
        const [projectsRes, testimonialsRes, homeContentRes, servicesContentRes] = await Promise.all([
          axios.get(`${API}/projects?featured=true`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/site-content/home`),
          axios.get(`${API}/site-content/services`),
        ]);
        
        setProjects(projectsRes.data.slice(0, 4));
        setTestimonials(testimonialsRes.data.slice(0, 3));
        
        // Set CMS content with fallbacks
        if (homeContentRes.data?.content) {
          setContent(prev => ({ ...prev, ...homeContentRes.data.content }));
        }
        if (servicesContentRes.data?.content?.list) {
          setServices(servicesContentRes.data.content.list);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={content.hero?.image_url || defaultContent.hero.image_url}
            alt="Commercial Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block animate-fade-in-up">
              {content.hero?.subtitle || defaultContent.hero.subtitle}
            </span>
            <h1 className="font-['Oswald'] text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up stagger-1">
              {content.hero?.title?.replace(content.hero?.highlight || defaultContent.hero.highlight, '').trim()}{" "}
              <span className="text-[#7ED4C6]">{content.hero?.highlight || defaultContent.hero.highlight}</span>
            </h1>
            <p className="text-[#A1A1AA] text-lg md:text-xl leading-relaxed mb-8 animate-fade-in-up stagger-2">
              {content.hero?.description || defaultContent.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link to="/consultation" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-schedule-btn">
                <Calendar size={18} />
                Schedule a Consultation
              </Link>
              <Link to="/projects" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="hero-projects-btn">
                View Our Work
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#7ED4C6] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#7ED4C6] rounded-full" />
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
                {content.about_preview?.subtitle || defaultContent.about_preview.subtitle}
              </span>
              <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-6">
                {content.about_preview?.title || defaultContent.about_preview.title}
              </h2>
              <div className="section-divider" />
              <p className="text-[#A1A1AA] text-lg leading-relaxed mb-6">
                {content.about_preview?.description || defaultContent.about_preview.description}
              </p>
              <ul className="space-y-4 mb-8">
                {(content.about_preview?.features || defaultContent.about_preview.features).map(
                  (item) => (
                    <li key={item} className="flex items-center gap-3 text-white">
                      <CheckCircle size={20} className="text-[#7ED4C6]" />
                      {item}
                    </li>
                  )
                )}
              </ul>
              <Link to="/about" className="btn-secondary inline-flex items-center gap-2" data-testid="about-learn-more">
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src={content.about_preview?.image_url || defaultContent.about_preview.image_url}
                alt="Construction Team"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#7ED4C6] p-8">
                <div className="stat-number">{content.about_preview?.years_experience || defaultContent.about_preview.years_experience}</div>
                <p className="text-[#0A0A0A] font-semibold uppercase tracking-wider">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#121212] border-y border-[#2A2A2A]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(content.stats || defaultContent.stats).map((stat, index) => (
              <div key={index} className="text-center">
                <div className="stat-number">{stat.number}</div>
                <p className="text-[#A1A1AA] uppercase tracking-wider text-sm mt-2">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              {content.services?.subtitle || defaultContent.services.subtitle}
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              {content.services?.title || defaultContent.services.title}
            </h2>
            <div className="section-divider mx-auto" />
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mt-4">
              {content.services?.description || defaultContent.services.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard 
                key={index} 
                service={{ 
                  ...service, 
                  icon: iconMap[service.icon] || HardHat 
                }} 
                index={index} 
              />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-services">
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 bg-[#121212]" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
            <div>
              <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
                Our Portfolio
              </span>
              <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
                Featured Projects
              </h2>
              <div className="section-divider" />
            </div>
            <Link to="/projects" className="btn-secondary mt-6 md:mt-0 inline-flex items-center gap-2" data-testid="view-all-projects">
              View All Projects
              <ArrowRight size={18} />
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-[#1E1E1E] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              Testimonials
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <div className="section-divider mx-auto" />
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-64 bg-[#121212] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#7ED4C6]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6">
            {content.cta?.title || defaultContent.cta.title}
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8 max-w-2xl mx-auto">
            {content.cta?.description || defaultContent.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="cta-schedule-btn"
            >
              <Calendar size={18} />
              Book a 30-Minute Call
            </Link>
            <a
              href="tel:862-350-7122"
              className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
              data-testid="cta-call-btn"
            >
              <Phone size={18} />
              Call 862-350-7122
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
